import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box } from "@chakra-ui/react";
import { getFacilityById, TIME_SLOTS, BOOKING_API } from "./facilityData";

const MONTH_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_ABBR = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function toDateStr(d) {
  return d.toISOString().split("T")[0];
}

export default function GroundBookingDetail() {
  const { facilityId } = useParams();
  const navigate = useNavigate();
  const facility = getFacilityById(facilityId);

  const today = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate]   = useState(null);
  const [selectedSlot, setSelectedSlot]   = useState(null);
  const [monthAvail, setMonthAvail]       = useState({});
  const [dayBookedSlots, setDayBookedSlots] = useState([]);
  const [loadingMonth, setLoadingMonth]   = useState(false);
  const [loadingDay, setLoadingDay]       = useState(false);

  // Redirect if unknown facilityId
  useEffect(() => {
    if (!facility) navigate("/ground-booking");
  }, []); // eslint-disable-line

  // Fetch month availability
  useEffect(() => {
    if (!facility) return;
    const mm = String(month + 1).padStart(2, "0");
    setLoadingMonth(true);
    fetch(`${BOOKING_API}/api/ground-booking/availability/${facilityId}?month=${year}-${mm}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          const map = {};
          data.availability.forEach(a => { map[a.date] = a; });
          setMonthAvail(map);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingMonth(false));
  }, [facilityId, month, year]); // eslint-disable-line

  // Fetch booked slots for selected date
  useEffect(() => {
    if (!selectedDate) return;
    setDayBookedSlots([]);
    setLoadingDay(true);
    fetch(`${BOOKING_API}/api/ground-booking/availability/${facilityId}?date=${selectedDate}`)
      .then(r => r.json())
      .then(data => { if (data.success) setDayBookedSlots(data.bookedSlots); })
      .catch(() => {})
      .finally(() => setLoadingDay(false));
  }, [selectedDate]); // eslint-disable-line

  if (!facility) return null;

  const todayStr = toDateStr(today);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const dates = Array.from({ length: daysInMonth }, (_, i) => {
    return toDateStr(new Date(Date.UTC(year, month, i + 1)));
  }).filter(d => d >= todayStr);

  const getDateStatus = (dateStr) => {
    if (dateStr === selectedDate) return "selected";
    const info = monthAvail[dateStr];
    if (info?.fullyBooked) return "booked";
    return "available";
  };

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const handleBookNow = () => {
    if (!selectedDate || !selectedSlot) {
      document.getElementById("date-section")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    const slotData = TIME_SLOTS.find(s => s.label === selectedSlot);
    navigate(`/ground-booking/${facilityId}/confirm`, {
      state: { facility, selectedDate, selectedSlot, startTime: slotData.startTime, endTime: slotData.endTime, durationHours: slotData.durationHours },
    });
  };

  const fmtDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return { day: d.getDate(), mon: MONTH_EN[d.getMonth()].slice(0, 3), dow: DAY_ABBR[d.getDay()] };
  };

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: "28px 16px", fontSize: "17px" }}>

      {/* Photo collage */}
      <Box mb={6} borderRadius={12} overflow="hidden">
        {/* Mobile: single hero image */}
        <Box display={{ base: "block", md: "none" }}>
          <img src={facility.images[0]} alt="" style={{ width: "100%", height: 220, objectFit: "cover", display: "block" }} />
        </Box>
        {/* Desktop: 2-col grid */}
        <Box display={{ base: "none", md: "grid" }} gridTemplateColumns="1fr 1fr" gridTemplateRows="190px 190px" gap="8px">
          <img src={facility.images[0]} alt="" style={{ gridRow: "1 / 3", width: "100%", height: "100%", objectFit: "cover" }} />
          <img src={facility.images[1]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <img src={facility.images[2]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </Box>
      </Box>

      {/* Name + stars + location */}
      <h1 style={{ color: "#0047ab", fontSize: "1.6rem", fontWeight: 700, marginBottom: 2 }}>
        {facility.nameHi}
        <span style={{ marginLeft: 10, color: "#f5a623", fontSize: "1.1rem", letterSpacing: 1 }}>{"★".repeat(facility.rating)}</span>
      </h1>
      <p style={{ color: "#555", marginBottom: 20, fontSize: "0.95rem" }}>📍 {facility.location}</p>

      {/* CTA buttons */}
      <div style={{ display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
        <button
          onClick={handleBookNow}
          style={{ background: "#0047ab", color: "#fff", border: "none", borderRadius: 6, padding: "10px 28px", fontSize: "16px", fontWeight: 700, cursor: "pointer" }}
        >
          अभी बुक करें
        </button>
        <a
          href="https://maps.google.com/maps?q=Patliputra+Sports+Complex+Patna"
          target="_blank"
          rel="noopener noreferrer"
          style={{ background: "#fff", color: "#0047ab", border: "1.5px solid #0047ab", borderRadius: 6, padding: "10px 28px", fontSize: "16px", fontWeight: 600, textDecoration: "none" }}
        >
          मैप पर देखें
        </a>
      </div>

      {/* Date & slot picker */}
      <div id="date-section">
        {/* Month navigation */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={prevMonth} style={navBtnStyle}>‹</button>
            <span style={{ fontWeight: 700, minWidth: 130, textAlign: "center" }}>{MONTH_EN[month]} {year}</span>
            <button onClick={nextMonth} style={navBtnStyle}>›</button>
          </div>
          {loadingMonth && <span style={{ fontSize: "14px", color: "#888" }}>Loading…</span>}
        </div>

        {/* Date strip */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
          {dates.map(d => {
            const status = getDateStatus(d);
            const { day, mon } = fmtDate(d);
            const isBooked   = status === "booked";
            const isSelected = status === "selected";
            return (
              <button
                key={d}
                disabled={isBooked}
                onClick={() => { setSelectedDate(d); setSelectedSlot(null); }}
                style={{
                  width: 68, padding: "9px 4px", borderRadius: 8, textAlign: "center",
                  border: isSelected ? "2px solid #28a745" : isBooked ? "2px solid #ccc" : "2px solid #ddd",
                  background: isSelected ? "#e6f9ec" : "#fff",
                  cursor: isBooked ? "not-allowed" : "pointer",
                  opacity: isBooked ? 0.5 : 1,
                  fontSize: "14px",
                }}
              >
                <div style={{ fontSize: "1.1rem", lineHeight: 1 }}>📅</div>
                <div style={{ fontWeight: 600, marginTop: 3 }}>{mon} {day}</div>
              </button>
            );
          })}
          {dates.length === 0 && (
            <p style={{ color: "#888", fontSize: "0.9rem" }}>इस महीने कोई उपलब्ध तिथि नहीं है।</p>
          )}
        </div>

        {/* Legend */}
        <div style={{ display: "flex", gap: 20, fontSize: "15px", color: "#555", marginBottom: 20, flexWrap: "wrap" }}>
          <span><span style={{ color: "#aaa" }}>⬤</span> Booked</span>
          <span><span style={{ color: "#28a745" }}>⬤</span> Selected</span>
          <span><span style={{ color: "#ccc" }}>○</span> Unselected</span>
        </div>

        {/* Time slots */}
        {selectedDate && (
          <div style={{ marginBottom: 28 }}>
            <h3 style={{ fontSize: "16px", fontWeight: 700, marginBottom: 10 }}>
              {fmtDate(selectedDate).mon} {fmtDate(selectedDate).day} के लिए समय स्लॉट चुनें
            </h3>
            {loadingDay ? (
              <p style={{ color: "#888" }}>उपलब्धता जाँच रहे हैं…</p>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {TIME_SLOTS.map(slot => {
                  const isBooked = dayBookedSlots.includes(slot.label);
                  const isChosen = selectedSlot === slot.label;
                  return (
                    <button
                      key={slot.id}
                      disabled={isBooked}
                      onClick={() => setSelectedSlot(slot.label)}
                      style={{
                        padding: "8px 18px", borderRadius: 20, fontSize: "0.9rem", fontWeight: 500, cursor: isBooked ? "not-allowed" : "pointer",
                        border: isChosen ? "2px solid #28a745" : isBooked ? "1.5px solid #ddd" : "1.5px solid #0047ab",
                        background: isChosen ? "#e6f9ec" : "#fff",
                        color: isBooked ? "#bbb" : isChosen ? "#28a745" : "#0047ab",
                        fontSize: "16px",
                      }}
                    >
                      {slot.label}
                    </button>
                  );
                })}
              </div>
            )}
            {selectedSlot && (
              <button
                onClick={handleBookNow}
                style={{ marginTop: 16, background: "#0047ab", color: "#fff", border: "none", borderRadius: 7, padding: "12px 36px", fontSize: "16px", fontWeight: 700, cursor: "pointer" }}
              >
                बुकिंग जारी रखें →
              </button>
            )}
          </div>
        )}
      </div>

      {/* Overview */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: "1.2rem", fontWeight: 700, color: "#222", borderBottom: "1px solid #eee", paddingBottom: 8, marginBottom: 12 }}>अवलोकन</h2>
        {facility.description.split("\n\n").map((para, i) => (
          <p key={i} style={{ color: "#444", lineHeight: 1.75, marginBottom: 10 }}>{para}</p>
        ))}
      </div>

      {/* Map */}
      <div style={{ borderRadius: 10, overflow: "hidden" }}>
        <iframe
          title="Location"
          src="https://maps.google.com/maps?q=Patliputra+Sports+Complex+Patna&output=embed"
          width="100%"
          height="340"
          style={{ border: 0, display: "block" }}
          allowFullScreen
          loading="lazy"
        />
      </div>
    </div>
  );
}

const navBtnStyle = {
  border: "1px solid #ddd",
  background: "#fff",
  borderRadius: 4,
  padding: "4px 12px",
  cursor: "pointer",
  fontSize: "1.1rem",
};
